import { Primary, SelectField } from '@cinch-labs/design-system'
import { SearchResponse } from '@cinch-nx/data-search'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { Datadog } from '@cinch-nx/landing-ui-shared'
import { useAnalytics } from '@cinch-nx/shared-analytics'
import { datadogRum } from '@datadog/browser-rum'
import React, { useEffect, useState } from 'react'

import { Bucket, Makes } from '../../../types/vehicles-data'
import { decorateOptions } from '../utils/decorate-options'
import styles from './filters.module.css'
import {
  addInitialFetchCacheResponse,
  getSearchButtonText,
  MONTHLY_PRICE,
  onMakeSelected,
  onModelSelected,
  onSubmit,
  TOTAL_PRICE,
  updateModelOptions,
  updateSearchButtonLabel,
  updateStockLevels,
} from './handlers'
import { Price } from './price/price'
import { createTrackers } from './tracking'
import { FieldOption } from './types'

const { Actions } = Datadog

export interface FiltersProps {
  totalStock: number
  makes: Makes[]
  searchResponse?: SearchResponse
}

export const Filters: React.FC<FiltersProps> = ({
  totalStock,
  makes,
  searchResponse,
}) => {
  const { trackEvent } = useAnalytics()
  const [radioChecked, setRadioChecked] = useState<
    'monthly-price' | 'total-price'
  >(MONTHLY_PRICE)
  const [maxTotalPrice, setMaxTotalPrice] = useState<number>(0)
  const [selectedMake, setSelectedMake] = useState<undefined | string>()
  const [selectedModel, setSelectedModel] = useState<undefined | string>()
  const [makeOptions, setMakeOptions] = useState<FieldOption[]>(
    decorateOptions(
      makes.map((make) => ({
        label: `${make.name} (${make.stock})`,
        value: make.name,
        count: `${make.stock}`,
      })),
      totalStock,
    ),
  )

  const newCarsTabEnabled =
    readFromEnv(Env.HomeHeroFormWithNewCarsTab) === 'true'

  const [searchButtonText, setSearchButtonText] = useState(
    getSearchButtonText(totalStock),
  )
  const [modelOptions, setModelOptions] = useState<FieldOption[]>([])
  const [priceBucket, setPriceBucket] = useState<Bucket>({})
  const [monthlyPriceBucket, setMonthlyPriceBucket] = useState<Bucket>({})

  if (searchResponse) {
    addInitialFetchCacheResponse(searchResponse)
  }

  const { onChange: handleOnChangeTracking, onSubmit: handleOnSubmitTracking } =
    createTrackers(trackEvent)

  useEffect(() => {
    updateSearchButtonLabel({
      selectedMake,
      selectedModel,
      radioChecked,
      maxTotalPrice,
      setButtonText: setSearchButtonText,
    }).catch((error) => {
      setSearchButtonText(getSearchButtonText(totalStock))
      console.error('Unable to update search button label cause:', error)
      datadogRum.addError(error)
    })
  }, [selectedMake, selectedModel, radioChecked, maxTotalPrice])

  useEffect(() => {
    updateStockLevels({
      selectedMake,
      selectedModel,
      maxTotalPrice,
      radioChecked,
      totalStock,
      setModelOptions,
      setMakeOptions,
      setPriceBucket,
      setMonthlyPriceBucket,
    }).catch((error) => {
      setMakeOptions(
        decorateOptions(
          makes.map((make) => ({
            label: `${make.name} (${make.stock})`,
            value: make.name,
            count: `${make.stock}`,
          })),
          totalStock,
        ),
      )
      setModelOptions([])
      console.error('Unable to stock levels:', error)
      datadogRum.addError(error)
    })
  }, [selectedMake, selectedModel, radioChecked, maxTotalPrice])

  useEffect(() => {
    updateModelOptions({ selectedMake, selectedModel, makes, setModelOptions })
  }, [selectedMake, selectedModel, makes])

  useEffect(() => {
    if (!selectedMake && selectedMake !== '') {
      setSelectedMake('')
    }
  }, [selectedMake])

  return (
    <form
      className={styles['form']}
      onSubmit={(event) =>
        onSubmit({
          handleOnSubmitTracking,
          maxTotalPrice,
          radioChecked,
          selectedMake,
          selectedModel,
          event,
        })
      }
    >
      {newCarsTabEnabled ? (
        <>
          <fieldset className={styles['radioGroup']}>
            <div className={styles['radioInputContainer']}>
              <label htmlFor={MONTHLY_PRICE} className={styles['priceLabel']}>
                <input
                  type="radio"
                  id={MONTHLY_PRICE}
                  name={MONTHLY_PRICE}
                  checked={radioChecked === MONTHLY_PRICE}
                  onChange={() => {
                    setRadioChecked(MONTHLY_PRICE)
                    setMaxTotalPrice(0)
                    handleOnChangeTracking({
                      trackEventLabel: 'monthly price',
                      value: `${MONTHLY_PRICE} checked`,
                      datadogActionName: Actions.CLICK_MONTHLY_PRICE_RADIO,
                    })
                  }}
                />
                Monthly price
              </label>
            </div>
            <div className={styles['radioInputContainer']}>
              <label htmlFor={TOTAL_PRICE} className={styles['priceLabel']}>
                <input
                  type="radio"
                  id={TOTAL_PRICE}
                  name={TOTAL_PRICE}
                  checked={radioChecked === TOTAL_PRICE}
                  onChange={() => {
                    setRadioChecked(TOTAL_PRICE)
                    setMaxTotalPrice(0)
                    handleOnChangeTracking({
                      trackEventLabel: 'total price',
                      value: `${TOTAL_PRICE} checked`,
                      datadogActionName: Actions.CLICK_TOTAL_PRICE_RADIO,
                    })
                  }}
                />
                Total price
              </label>
            </div>
          </fieldset>
          <Price
            radioChecked={radioChecked}
            handleOnChangeTracking={handleOnChangeTracking}
            maxTotalPrice={maxTotalPrice}
            setMaxTotalPrice={setMaxTotalPrice}
            priceBucket={priceBucket}
            monthlyPriceBucket={monthlyPriceBucket}
          />
          <SelectField
            label="Select make"
            labelHidden
            id="make"
            name="make"
            data-testid="make-select"
            options={makeOptions}
            placeholder={'Select make'}
            placeholderValue={'Any'}
            value={selectedMake}
            onChange={(event) =>
              onMakeSelected({
                setSelectedMake,
                setSelectedModel,
                setMakeOptions,
                handleOnChangeTracking,
                event,
              })
            }
            error={{
              show: false,
              classNames: { container: styles['errorBox'] },
            }}
          />
          <SelectField
            label="Select model"
            labelHidden
            id="model"
            name="model"
            data-testid="model-select"
            options={modelOptions}
            placeholder={'Select model'}
            placeholderValue={'Any'}
            value={selectedModel}
            onChange={(event) =>
              onModelSelected({
                setSelectedModel,
                handleOnChangeTracking,
                event,
              })
            }
            disabled={
              !selectedMake || selectedMake.toLocaleLowerCase() === 'any'
            }
            error={{
              show: false,
              classNames: { container: styles['errorBox'] },
            }}
          />
        </>
      ) : (
        <>
          {' '}
          <SelectField
            label="Select make"
            labelHidden
            id="make"
            name="make"
            data-testid="make-select"
            options={makeOptions}
            placeholder={'Select make'}
            placeholderValue={'Any'}
            value={selectedMake}
            onChange={(event) =>
              onMakeSelected({
                setSelectedMake,
                setSelectedModel,
                setMakeOptions,
                handleOnChangeTracking,
                event,
              })
            }
            error={{
              show: false,
              classNames: { container: styles['errorBox'] },
            }}
          />
          <SelectField
            label="Select model"
            labelHidden
            id="model"
            name="model"
            data-testid="model-select"
            options={modelOptions}
            placeholder={'Select model'}
            placeholderValue={'Any'}
            value={selectedModel}
            onChange={(event) =>
              onModelSelected({
                setSelectedModel,
                handleOnChangeTracking,
                event,
              })
            }
            disabled={
              !selectedMake || selectedMake.toLocaleLowerCase() === 'any'
            }
            error={{
              show: false,
              classNames: { container: styles['errorBox'] },
            }}
          />
          <fieldset className={styles['radioGroup']}>
            <div className={styles['radioInputContainer']}>
              <label htmlFor={MONTHLY_PRICE} className={styles['priceLabel']}>
                <input
                  type="radio"
                  id={MONTHLY_PRICE}
                  name={MONTHLY_PRICE}
                  checked={radioChecked === MONTHLY_PRICE}
                  onChange={() => {
                    setRadioChecked(MONTHLY_PRICE)
                    setMaxTotalPrice(0)
                    handleOnChangeTracking({
                      trackEventLabel: 'monthly price',
                      value: `${MONTHLY_PRICE} checked`,
                      datadogActionName: Actions.CLICK_MONTHLY_PRICE_RADIO,
                    })
                  }}
                />
                Monthly price
              </label>
            </div>
            <div className={styles['radioInputContainer']}>
              <label htmlFor={TOTAL_PRICE} className={styles['priceLabel']}>
                <input
                  type="radio"
                  id={TOTAL_PRICE}
                  name={TOTAL_PRICE}
                  checked={radioChecked === TOTAL_PRICE}
                  onChange={() => {
                    setRadioChecked(TOTAL_PRICE)
                    setMaxTotalPrice(0)
                    handleOnChangeTracking({
                      trackEventLabel: 'total price',
                      value: `${TOTAL_PRICE} checked`,
                      datadogActionName: Actions.CLICK_TOTAL_PRICE_RADIO,
                    })
                  }}
                />
                Total price
              </label>
            </div>
          </fieldset>
          <Price
            radioChecked={radioChecked}
            handleOnChangeTracking={handleOnChangeTracking}
            maxTotalPrice={maxTotalPrice}
            setMaxTotalPrice={setMaxTotalPrice}
            priceBucket={priceBucket}
            monthlyPriceBucket={monthlyPriceBucket}
          />
        </>
      )}
      <div className={styles['buttonContainer']}>
        <Primary
          type="submit"
          classNames={{ button: styles['button'] }}
          data-testid="searchButton"
          isFullWidth
        >
          {searchButtonText}
        </Primary>
      </div>
    </form>
  )
}
